// src/pages/home/TasksGrid.tsx - Enhanced v2.0
import React, { useState, useMemo, memo, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  Skeleton,
  Fade,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import CommentIcon from "@mui/icons-material/Comment";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import LaunchIcon from "@mui/icons-material/Launch";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FolderIcon from "@mui/icons-material/Folder";
import CategoryIcon from "@mui/icons-material/Category";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";

import {
  getTaskStatusContainerBgColor,
  getTaskStatusLabel,
} from "../../utils/task-status.utils";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";
import { handleDate } from "../../utils/date-handlers";
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { baseUrl } from "../../constants";

// Shared types
import type { Task, MaybeUser } from "../../utils/task";

/* --------------------------- Safe alpha helper ---------------------------- */
const safeAlpha = (theme: any, color: string, opacity: number) => {
  try {
    return alpha(color, opacity);
  } catch {
    const fromPalette = (theme.palette as any)?.[color]?.main;
    if (fromPalette) {
      try {
        return alpha(fromPalette, opacity);
      } catch {}
    }
    const map: Record<string, string> = {
      orange: "#FFA726",
      red: "#EF5350",
      green: "#66BB6A",
      blue: "#42A5F5",
      grey: "#9E9E9E",
      gray: "#9E9E9E",
      yellow: "#FFEE58",
      purple: "#AB47BC",
      pink: "#EC407A",
      teal: "#26A69A",
      indigo: "#5C6BC0",
      amber: "#FFB300",
    };
    const hex = map[color?.toLowerCase?.() ?? ""] || color;
    try {
      return alpha(hex, opacity);
    } catch {
      return hex;
    }
  }
};

/* ---------------------------- Visual helpers ------------------------------ */
// Consistent pleasant color derived from a string (e.g., project name)
const colorFromString = (str: string, theme: any) => {
  if (!str) return theme.palette.primary.main;
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 65% ${theme.palette.mode === "dark" ? "62%" : "44%"})`;
};


const dateRangeLabel = (start?: string | null, end?: string | null) => {
  const s = start ? new Date(start) : null;
  const e = end ? new Date(end) : null;
  const fmt = (d: Date) => d.toLocaleDateString();
  if (s && e) return `${fmt(s)} → ${fmt(e)}`;
  if (s) return `Start ${fmt(s)}`;
  if (e) return `End ${fmt(e)}`;
  return "No dates";
};

/* ---------------------------------- Props --------------------------------- */
interface TasksGridProps {
  rows: Task[];
  isLoading: boolean;
  handleComment: (id: number) => void;
  handlePrivacy: (id: number, privacy: boolean) => void;
  handleFollow: (id: number, isFollowed: boolean) => void;
  handleEdit: (id: number) => void;
  handleAttach: (id: number) => void;
  currentUserId: number;
  density?: "comfortable" | "compact";
  onRefresh?: () => void;
}

/* --------------------------------- Helpers -------------------------------- */
const ImportanceIcon: React.FC<{ importance: string | TaskImportance }> = ({
  importance,
}) => {
  const imp = String(importance || "").toUpperCase();
  const sx = { fontSize: 16, color: "currentColor" };
  if (imp === "CRITICAL" || imp === "VERY_IMPORTANT") {
    return <ErrorOutlineIcon sx={sx} />;
  }
  if (imp === "IMPORTANT") {
    return <ReportProblemOutlinedIcon sx={sx} />;
  }
  return <InfoOutlinedIcon sx={sx} />;
};

const StatusIcon: React.FC<{ status: string | TaskStatus }> = ({ status }) => {
  const stat = String(status || "").toUpperCase();
  const sx = { fontSize: 12, color: "currentColor" };
  if (stat === "DONE") return <CheckCircleIcon sx={sx} />;
  if (stat === "IN_PROGRESS") return <PlayCircleFilledIcon sx={sx} />;
  if (stat === "REVIEW") return <VisibilityIcon sx={sx} />;
  if (stat === "POSTPONED") return <PauseCircleIcon sx={sx} />;
  return <ScheduleIcon sx={sx} />; // TODO
};

const getFollowerState = (task: Task, me: number) => {
  if (typeof task?.followedByMe === "boolean") return task.followedByMe;
  const list =
    (task.taskFollowers as any[]) || (task as any).TaskFollower || [];
  return list.some(
    (f: any) => f?.userId === me || f?.id === me || f?.user?.id === me
  );
};

const getImg = (u?: MaybeUser | null) =>
  u?.imageUrl || (u as any)?.profile?.imageUrl || undefined;

const countFromArrayKeys = (obj: any, arrayKeys: string[]) => {
  for (const k of arrayKeys) {
    if (Array.isArray(obj?.[k])) return obj[k].length;
  }
  return 0;
};

const countSafely = (
  task: Task,
  countKey: "taskComments" | "taskAttachments" | "taskFollowers",
  arrayKeys: string[],
  numericFallbackKeys: string[]
) => {
  const c = task?._count?.[countKey as keyof NonNullable<Task["_count"]>];
  if (typeof c === "number") return c as number;
  const arrCount = countFromArrayKeys(task, arrayKeys);
  if (arrCount) return arrCount;
  for (const k of numericFallbackKeys) {
    const v = (task as any)?.[k];
    if (typeof v === "number") return v;
  }
  return 0;
};

const MAX_ASSIGNEE_AVATARS = 3;

/* -------------------------------- Memoized Task Card ------------------------------- */
interface TaskCardProps {
  task: Task;
  currentUserId: number;
  density: "comfortable" | "compact";
  theme: any;
  onComment: (id: number) => void;
  onPrivacy: (id: number, privacy: boolean) => void;
  onFollow: (id: number, isFollowed: boolean) => void;
  onEdit: (id: number) => void;
  onAttach: (id: number) => void;
  onNavigate: (id: number) => void;
  onOpenAssignees: (users: MaybeUser[], taskTitle?: string) => void;
  onOpenStatusChanger: (taskId: number, current: TaskStatus) => void;
  onOpenDates: (
    start?: string | null,
    end?: string | null,
    title?: string
  ) => void;
}

const TaskCard = memo<TaskCardProps>(
  ({
    task,
    currentUserId,
    density,
    theme,
    onComment,
    onPrivacy,
    onFollow,
    onEdit,
    onAttach,
    onNavigate,
    onOpenAssignees,
    onOpenStatusChanger,
    onOpenDates,
  }) => {
    const compact = density === "compact";
    const cardPadding = compact ? 1.25 : 2;
    const minCardHeight = compact ? 280 : 340;
    const titleSize = compact ? "0.98rem" : "1.06rem";
    const descSize = compact ? "0.82rem" : "0.88rem";

    const isFollowed = getFollowerState(task, currentUserId);

    const commentCount = countSafely(
      task,
      "taskComments",
      ["taskComments", "TaskComment"],
      ["commentCount"]
    );
    const attachCount = countSafely(
      task,
      "taskAttachments",
      ["taskAttachments", "TaskAttachment"],
      ["attachmentCount"]
    );
    const followerCount = countSafely(
      task,
      "taskFollowers",
      ["taskFollowers", "TaskFollower"],
      ["followerCount"]
    );

    const end = task.endDate ? new Date(task.endDate) : new Date(NaN);
    const overdue =
      task?.status !== TaskStatus.DONE &&
      task?.status !== TaskStatus.REVIEW &&
      !isNaN(end.getTime()) &&
      end.getTime() < Date.now();

    const reminder = (task as any).remindIn ?? (task as any).remindInts;
    const isSub = task?.parentTaskId != null;

    // Get userTask status for current user
    const taskData = task as any;
    const assignedUsers = taskData.assignedUsers || [];
    const userTask = assignedUsers.find((au: any) => au.userId === currentUserId || au.user?.id === currentUserId);
    const userTaskStatus = userTask?.status || task.status; // Fallback to task status if not found

    // Colors - use userTaskStatus instead of task.status
    const statusBase = getTaskStatusContainerBgColor(userTaskStatus as TaskStatus);
    const leftAccent =
      theme.palette.mode === "dark"
        ? safeAlpha(theme, statusBase, 0.85)
        : safeAlpha(theme, statusBase, 0.9);
    const rawStatusBg = getTaskStatusContainerBgColor(
      userTaskStatus as TaskStatus
    );
    const statusBg =
      theme.palette.mode === "dark"
        ? safeAlpha(theme, rawStatusBg, 0.7)
        : rawStatusBg;

    const impColor = getImportanceContainerColor(
      task.importance as TaskImportance
    );
    const impLabel = getImportanceContainerText(
      task.importance as TaskImportance
    );
    const impBorder = safeAlpha(theme, impColor, 0.45);
    const impText = impColor;

    // Project hue
    const projColor = colorFromString(task.projectName || "", theme);

    // Status text color - Enhanced for better contrast
    const statusTextColor =
      theme.palette.mode === "dark"
        ? theme.palette.grey[50]
        : "#FFFFFF";

    // Overdue gradient
    const overdueGradient = overdue
      ? `linear-gradient(90deg, ${alpha(
          theme.palette.error.main,
          0.08
        )} 0%, transparent 35%)`
      : "none";


    return (
      <Grid 
        item 
        xs={12} 
        sm={6} 
        md={4} 
        lg={3} 
        xl={2.4 as any}
        sx={{
          // Ensure buttons are not clipped by grid container
          overflow: "visible",
          position: "relative",
        }}
      >
        <Fade in timeout={300}>
          <Box
            role="button"
            tabIndex={0}
            aria-label={`Open ${task.title}`}
            onKeyDown={(e) => e.key === "Enter" && onNavigate(task.id)}
            onClick={() => onNavigate(task.id)}
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              minHeight: minCardHeight,
              p: cardPadding,
              borderRadius: 3,
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.paper, 0.95)
                  : alpha(theme.palette.background.paper, 0.98),
              border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,.3), 0 2px 8px rgba(0,0,0,.2)"
                  : "0 12px 32px rgba(0,0,0,.08), 0 2px 8px rgba(0,0,0,.04)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 16px 48px rgba(0,0,0,.4), 0 4px 16px rgba(0,0,0,.3)"
                    : "0 20px 48px rgba(0,0,0,.12), 0 4px 16px rgba(0,0,0,.08)",
                transform: "translateY(-4px)",
                border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
              },
              outline: "none",
              cursor: "default",
              borderLeft: `4px solid ${leftAccent}`,
              backgroundImage: overdueGradient,
              backgroundRepeat: "no-repeat",
              backdropFilter: "saturate(180%) blur(8px)",
              // Ensure buttons are not clipped
              overflow: "visible",
              "&:focus-visible": {
                boxShadow: `0 0 0 3px ${alpha(
                  theme.palette.primary.main,
                  0.4
                )}`,
              },
              animation: "cardIn .3s cubic-bezier(0.4, 0, 0.2, 1)",
              "@keyframes cardIn": {
                from: { opacity: 0, transform: "translateY(8px) scale(0.98)" },
                to: { opacity: 1, transform: "translateY(0) scale(1)" },
              },
              // Add subtle gradient overlay for depth
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 3,
                background: theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)"
                  : "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)",
                pointerEvents: "none",
                zIndex: 0,
              },
              // Ensure all direct children have proper z-index except top-right buttons
              "& > *:not([data-top-right-buttons])": {
                position: "relative",
                zIndex: 1,
              },
            }}
          >
            {/* Top-right: status + quick actions - Professional Design */}
            <Box
              data-top-right-buttons
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                zIndex: 1000,
                pointerEvents: "auto",
                opacity: 1,
                visibility: "visible",
              }}
            >
              {/* Minimal Beautiful Status Badge */}
              <Tooltip title={`My Status: ${getTaskStatusLabel(userTaskStatus as TaskStatus)} (click to change)`} arrow placement="top">
                <Box
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenStatusChanger(task.id, userTaskStatus as TaskStatus);
                  }}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.2,
                    py: 0.45,
                    borderRadius: 1.5,
                    background: theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(statusBg, 0.75)} 0%, ${alpha(statusBg, 0.6)} 100%)`
                      : statusBg,
                    border: `1px solid ${alpha(statusTextColor, theme.palette.mode === "dark" ? 0.35 : 0.2)}`,
                    backdropFilter: "blur(8px)",
                    boxShadow: theme.palette.mode === "dark"
                      ? `0 2px 8px ${alpha(statusBg, 0.25)}`
                      : `0 2px 8px ${alpha(statusBg, 0.2)}`,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-1px) scale(1.03)",
                      boxShadow: theme.palette.mode === "dark"
                        ? `0 4px 12px ${alpha(statusBg, 0.35)}`
                        : `0 4px 12px ${alpha(statusBg, 0.3)}`,
                      border: `1px solid ${alpha(statusTextColor, theme.palette.mode === "dark" ? 0.5 : 0.35)}`,
                    },
                    "&:active": {
                      transform: "scale(0.98)",
                    },
                  }}
                >
                  <StatusIcon status={userTaskStatus} />
                  <Typography
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: statusTextColor,
                      letterSpacing: 0.3,
                      textTransform: "uppercase",
                      lineHeight: 1,
                    }}
                  >
                    {getTaskStatusLabel(userTaskStatus as TaskStatus)}
                  </Typography>
                  {userTaskStatus === TaskStatus.IN_PROGRESS && (
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        bgcolor: statusTextColor,
                        flexShrink: 0,
                        boxShadow: `0 0 6px ${alpha(statusTextColor, 0.8)}`,
                        animation: "statusPulse 1.5s infinite",
                        "@keyframes statusPulse": {
                          "0%, 100%": { opacity: 1, transform: "scale(1)" },
                          "50%": { opacity: 0.4, transform: "scale(1.3)" },
                        },
                      }}
                    />
                  )}
                </Box>
              </Tooltip>

              {/* Vertical stack of action buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  alignItems: "flex-end",
                }}
              >
                {/* Elegant Open Task Button */}
                <Tooltip title="Open Task" arrow placement="left">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onNavigate(task.id);
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      background: theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                      border: `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                      backdropFilter: "blur(8px)",
                      boxShadow: theme.palette.mode === "dark"
                        ? `0 3px 12px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 0.5px 0 ${alpha("#fff", 0.12)}`
                        : `0 3px 12px ${alpha(theme.palette.primary.main, 0.18)}, inset 0 0.5px 0 ${alpha("#fff", 0.7)}`,
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background: theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
                        transform: "translateY(-2px) scale(1.1) rotate(8deg)",
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}, inset 0 1px 0 ${alpha("#fff", 0.18)}`
                          : `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}, inset 0 1px 0 ${alpha("#fff", 0.85)}`,
                        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.7)}`,
                      },
                      "&:active": {
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <LaunchIcon 
                      sx={{ 
                        fontSize: 17,
                        color: theme.palette.primary.main,
                        filter: theme.palette.mode === "dark"
                          ? `drop-shadow(0 0 4px ${alpha(theme.palette.primary.main, 0.5)})`
                          : "none",
                        transition: "all 0.25s ease",
                      }} 
                    />
                  </IconButton>
                </Tooltip>

                {/* More Options Button */}
                <Tooltip title="More Actions" arrow placement="left">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      background: theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.1)} 0%, ${alpha(theme.palette.text.primary, 0.05)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.06)} 0%, ${alpha(theme.palette.text.primary, 0.03)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
                      backdropFilter: "blur(6px)",
                      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        background: theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.18)} 0%, ${alpha(theme.palette.text.primary, 0.12)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.12)} 0%, ${alpha(theme.palette.text.primary, 0.08)} 100%)`,
                        transform: "scale(1.12) rotate(90deg)",
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 4px 16px ${alpha("#000", 0.4)}`
                          : `0 4px 16px ${alpha("#000", 0.12)}`,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      },
                      "&:active": {
                        transform: "scale(1) rotate(90deg)",
                      },
                    }}
                  >
                    <MoreHorizIcon 
                      sx={{ 
                        fontSize: 18,
                        color: theme.palette.text.secondary,
                        transition: "all 0.2s ease",
                      }} 
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>


            {/* Header: creator - Clean & Professional */}
            <Box
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pr: 11,
              }}
            >
              <Avatar
                src={getImg(task?.createdBy)}
                sx={{ 
                  width: 32, 
                  height: 32, 
                  boxShadow: theme.palette.mode === "dark"
                    ? `0 2px 8px ${alpha("#000", 0.4)}, 0 0 0 2px ${alpha(theme.palette.background.paper, 1)}`
                    : `0 2px 8px ${alpha("#000", 0.15)}, 0 0 0 2px ${alpha(theme.palette.background.paper, 1)}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: theme.palette.mode === "dark"
                      ? `0 4px 12px ${alpha("#000", 0.5)}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`
                      : `0 4px 12px ${alpha("#000", 0.2)}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`,
                  }
                }}
              >
                {task?.createdBy?.username?.charAt(0)}
              </Avatar>

              <Typography
                fontWeight={700}
                fontSize="0.88rem"
                noWrap
                title={task?.createdBy?.username}
                sx={{ 
                  flex: 1, 
                  minWidth: 0,
                  color: theme.palette.text.primary,
                  letterSpacing: 0.2,
                }}
              >
                {task?.createdBy?.username}
              </Typography>
            </Box>

            {/* Project & Type Info Section - Enhanced Design */}
            {(task.projectName || task.type) && (
              <Box
                sx={{
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {task.projectName && (
                  <Tooltip title={`Project: ${task.projectName}`} arrow placement="top">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.75,
                        py: 0.75,
                        maxWidth: 150,
                        borderRadius: 2.5,
                        background: theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                        backdropFilter: "blur(8px)",
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}, inset 0 1px 0 ${alpha("#fff", 0.1)}`
                          : `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.8)}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "default",
                        overflow: "hidden",
                        "&:hover": {
                          maxWidth: "fit-content",
                          background: theme.palette.mode === "dark"
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.18)} 100%)`
                            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.12)} 100%)`,
                          transform: "translateY(-2px) scale(1.03)",
                          boxShadow: theme.palette.mode === "dark"
                            ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 1px 0 ${alpha("#fff", 0.15)}`
                            : `0 6px 20px ${alpha(theme.palette.primary.main, 0.18)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
                          zIndex: 10,
                        },
                      }}
                    >
                      <FolderIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.primary.main,
                          filter: theme.palette.mode === "dark"
                            ? `drop-shadow(0 0 4px ${alpha(theme.palette.primary.main, 0.5)})`
                            : "none",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                          fontSize: "0.78rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          letterSpacing: 0.3,
                          textShadow: theme.palette.mode === "dark"
                            ? `0 1px 2px ${alpha("#000", 0.3)}`
                            : "none",
                          transition: "all 0.3s ease",
                          ".MuiBox-root:hover &": {
                            whiteSpace: "normal",
                          },
                        }}
                      >
                        {task.projectName}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                
                {task.type && (
                  <Tooltip title={`Type: ${task.type}`} arrow placement="top">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        px: 1.75,
                        py: 0.75,
                        maxWidth: 140,
                        borderRadius: 2.5,
                        background: theme.palette.mode === "dark"
                          ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.25)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`
                          : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                        border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.4)}`,
                        backdropFilter: "blur(8px)",
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}, inset 0 1px 0 ${alpha("#fff", 0.1)}`
                          : `0 4px 12px ${alpha(theme.palette.secondary.main, 0.1)}, inset 0 1px 0 ${alpha("#fff", 0.8)}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "default",
                        overflow: "hidden",
                        "&:hover": {
                          maxWidth: "fit-content",
                          background: theme.palette.mode === "dark"
                            ? `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.35)} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`
                            : `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.22)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                          transform: "translateY(-2px) scale(1.03)",
                          boxShadow: theme.palette.mode === "dark"
                            ? `0 6px 20px ${alpha(theme.palette.secondary.main, 0.25)}, inset 0 1px 0 ${alpha("#fff", 0.15)}`
                            : `0 6px 20px ${alpha(theme.palette.secondary.main, 0.18)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
                          zIndex: 10,
                        },
                      }}
                    >
                      <CategoryIcon
                        sx={{
                          fontSize: 16,
                          color: theme.palette.secondary.main,
                          filter: theme.palette.mode === "dark"
                            ? `drop-shadow(0 0 4px ${alpha(theme.palette.secondary.main, 0.5)})`
                            : "none",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.secondary.main,
                          fontSize: "0.78rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          letterSpacing: 0.3,
                          textShadow: theme.palette.mode === "dark"
                            ? `0 1px 2px ${alpha("#000", 0.3)}`
                            : "none",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {task.type}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}

            {/* Title */}
            <Tooltip title={task.title}>
              <Typography
                color="primary.main"
                fontWeight={900}
                fontSize={titleSize}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mb: 1,
                  letterSpacing: 0.12,
                  pr: 12,
                  lineHeight: 1.4,
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: theme.palette.primary.dark,
                  },
                }}
              >
                {task.title}
              </Typography>
            </Tooltip>

            {/* Description - Enhanced Design */}
            {task.description && (
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.5,
                  borderRadius: 2.5,
                  background: theme.palette.mode === "dark"
                    ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha(theme.palette.info.main, 0.03)} 100%)`
                    : `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha("#000", 0.01)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  backdropFilter: "blur(4px)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background: theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.12)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.08)} 0%, ${alpha("#000", 0.02)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    transform: "translateY(-1px)",
                    boxShadow: theme.palette.mode === "dark"
                      ? `0 4px 12px ${alpha("#000", 0.2)}`
                      : `0 4px 12px ${alpha("#000", 0.05)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <DescriptionIcon
                    sx={{
                      fontSize: 16,
                      color: theme.palette.mode === "dark"
                        ? theme.palette.info.light
                        : theme.palette.info.main,
                      mt: 0.2,
                      flexShrink: 0,
                      opacity: 0.7,
                    }}
                  />
                  <Tooltip title={task.description} arrow>
                    <Typography
                      fontSize={descSize}
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.5,
                        color: theme.palette.mode === "dark"
                          ? alpha(theme.palette.text.primary, 0.85)
                          : theme.palette.text.secondary,
                        fontWeight: 500,
                        letterSpacing: 0.1,
                      }}
                    >
                      {task.description}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Meta row - Enhanced modern design */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.5,
                p: 1.25,
                borderRadius: 2.5,
                background: theme.palette.mode === "dark" 
                  ? `linear-gradient(135deg, ${alpha("#fff", 0.04)} 0%, ${alpha("#fff", 0.01)} 100%)`
                  : `linear-gradient(135deg, ${alpha("#000", 0.02)} 0%, ${alpha("#fff", 0.5)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                backdropFilter: "blur(10px)",
                boxShadow: theme.palette.mode === "dark"
                  ? `0 4px 16px ${alpha("#000", 0.2)}, inset 0 1px 0 ${alpha("#fff", 0.05)}`
                  : `0 4px 16px ${alpha("#000", 0.04)}, inset 0 1px 0 ${alpha("#fff", 0.8)}`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: theme.palette.mode === "dark" 
                    ? `linear-gradient(135deg, ${alpha("#fff", 0.06)} 0%, ${alpha("#fff", 0.02)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#000", 0.03)} 0%, ${alpha("#fff", 0.6)} 100%)`,
                  boxShadow: theme.palette.mode === "dark"
                    ? `0 6px 20px ${alpha("#000", 0.3)}, inset 0 1px 0 ${alpha("#fff", 0.08)}`
                    : `0 6px 20px ${alpha("#000", 0.06)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  minWidth: 0,
                  maxWidth: "74%",
                }}
              >
                {/* Overdue Badge - Enhanced with glow */}
                {overdue && (
                  <Tooltip title="This task is overdue">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 999,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.2)} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`,
                        border: `1.5px solid ${alpha(theme.palette.error.main, 0.5)}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        animation: "overduePulse 2s infinite",
                        "@keyframes overduePulse": {
                          "0%": { 
                            boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.5)}, 0 2px 12px ${alpha(theme.palette.error.main, 0.3)}` 
                          },
                          "70%": { 
                            boxShadow: `0 0 0 4px ${alpha(theme.palette.error.main, 0)}, 0 2px 12px ${alpha(theme.palette.error.main, 0.1)}` 
                          },
                          "100%": { 
                            boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}, 0 2px 12px ${alpha(theme.palette.error.main, 0.3)}` 
                          },
                        },
                        "&:hover": {
                          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.3)} 0%, ${alpha(theme.palette.error.main, 0.15)} 100%)`,
                          transform: "scale(1.08)",
                          boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.4)}`,
                        },
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          bgcolor: theme.palette.error.main,
                          flexShrink: 0,
                          boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.8)}`,
                          animation: "blink 1s infinite",
                          "@keyframes blink": {
                            "0%, 50%": { opacity: 1 },
                            "51%, 100%": { opacity: 0.4 },
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 900,
                          color: theme.palette.error.main,
                          fontSize: 10,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          textShadow: theme.palette.mode === "dark" 
                            ? `0 1px 4px ${alpha(theme.palette.error.main, 0.3)}`
                            : "none",
                        }}
                      >
                        Overdue
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                {/* Importance pill - Enhanced glassmorphism */}
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 999,
                    border: `1.5px solid ${impBorder}`,
                    background: `linear-gradient(135deg, ${safeAlpha(theme, impColor, 0.15)} 0%, ${safeAlpha(theme, impColor, 0.08)} 100%)`,
                    color: impText,
                    fontWeight: 900,
                    fontSize: 11,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.6,
                    flexShrink: 0,
                    backdropFilter: "blur(8px)",
                    boxShadow: theme.palette.mode === "dark" 
                      ? `0 3px 12px ${safeAlpha(theme, impBorder, 0.25)}, inset 0 1px 0 ${alpha("#fff", 0.1)}`
                      : `0 3px 12px ${safeAlpha(theme, impBorder, 0.15)}, inset 0 1px 0 ${alpha("#fff", 0.8)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "scale(1.08) translateY(-1px)",
                      boxShadow: theme.palette.mode === "dark" 
                        ? `0 6px 20px ${safeAlpha(theme, impBorder, 0.35)}, inset 0 1px 0 ${alpha("#fff", 0.15)}`
                        : `0 6px 20px ${safeAlpha(theme, impBorder, 0.25)}, inset 0 1px 0 ${alpha("#fff", 0.9)}`,
                      background: `linear-gradient(135deg, ${safeAlpha(theme, impColor, 0.2)} 0%, ${safeAlpha(theme, impColor, 0.12)} 100%)`,
                    },
                  }}
                  title={impLabel}
                >
                  <ImportanceIcon importance={task.importance} />
                  <span>{impLabel}</span>
                </Box>

                {/* Parent/Sub chip - Enhanced with gradient */}
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: 999,
                    border: `1.5px solid ${alpha(theme.palette.text.primary, 0.25)}`,
                    background: theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.12)} 0%, ${alpha(theme.palette.text.primary, 0.06)} 100%)`
                      : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.08)} 0%, ${alpha(theme.palette.text.primary, 0.04)} 100%)`,
                    color: theme.palette.text.primary,
                    fontSize: 10,
                    fontWeight: 800,
                    flexShrink: 0,
                    backdropFilter: "blur(4px)",
                    boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.08)}`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      background: theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.18)} 0%, ${alpha(theme.palette.text.primary, 0.1)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.text.primary, 0.12)} 0%, ${alpha(theme.palette.text.primary, 0.06)} 100%)`,
                      transform: "scale(1.05)",
                      boxShadow: `0 3px 12px ${alpha(theme.palette.text.primary, 0.12)}`,
                    },
                  }}
                  title={isSub ? "Sub Task" : "Parent Task"}
                >
                  {isSub ? "Sub" : "Parent"}
                </Box>

                {/* Parent title / Subtask count */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                  }}
                  title={
                    task?.parentTask?.title
                      ? task.parentTask.title
                      : `Subtasks: ${task?.subTaskCount ?? 0}`
                  }
                >
                  {task?.parentTask?.title ? (
                    <>
                      <SubdirectoryArrowRightIcon sx={{ fontSize: 12 }} />
                      <strong>{task.parentTask.title}</strong>
                    </>
                  ) : (
                    <strong>Subtasks: {task?.subTaskCount ?? 0}</strong>
                  )}
                </Typography>
              </Box>

              {/* Dates quick view - Enhanced button */}
              <Tooltip title={dateRangeLabel(task.startDate, task.endDate)} arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenDates(task.startDate, task.endDate, task.title);
                  }}
                  sx={{
                    bgcolor: theme.palette.mode === "dark" 
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    backdropFilter: "blur(8px)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      bgcolor: theme.palette.mode === "dark" 
                        ? alpha(theme.palette.primary.main, 0.25)
                        : alpha(theme.palette.primary.main, 0.15),
                      transform: "scale(1.15) rotate(5deg)",
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                  }}
                >
                  <CalendarMonthIcon 
                    sx={{ 
                      fontSize: 18,
                      color: theme.palette.primary.main,
                    }} 
                  />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Footer: assignees + actions - Enhanced modern design */}
            <Box
              sx={{
                mt: "auto",
                pt: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 44,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              }}
            >
              {/* Assignees (enhanced stacked avatars with glow) */}
              <Box 
                display="flex" 
                alignItems="center" 
                sx={{ 
                  '& > *': { marginLeft: '-10px' }, 
                  '& > *:first-of-type': { marginLeft: 0 },
                  position: 'relative',
                }}
              >
                {((task.assignedUsers as MaybeUser[]) ?? [])
                  .slice(0, MAX_ASSIGNEE_AVATARS)
                  .map((u: MaybeUser, idx: number, arr: MaybeUser[]) => (
                    <Tooltip key={u?.user?.id ?? idx} title={u?.user?.username ?? ""} arrow>
                      <Avatar
                        src={u?.user?.profile?.imageUrl || undefined}
                        sx={{
                          width: 28,
                          height: 28,
                          border: `2.5px solid ${theme.palette.background.paper}`,
                          boxShadow: theme.palette.mode === "dark"
                            ? `0 0 0 1px ${alpha(projColor, 0.4)}, 0 4px 12px ${alpha("#000", 0.4)}, 0 0 16px ${alpha(projColor, 0.2)}`
                            : `0 0 0 1px ${alpha(projColor, 0.5)}, 0 4px 12px ${alpha("#000", 0.12)}, 0 0 12px ${alpha(projColor, 0.15)}`,
                          zIndex: arr.length - idx,
                          fontSize: 12,
                          fontWeight: 700,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "scale(1.2) translateY(-2px)",
                            zIndex: 999,
                            boxShadow: theme.palette.mode === "dark"
                              ? `0 0 0 2px ${alpha(projColor, 0.6)}, 0 8px 20px ${alpha("#000", 0.5)}, 0 0 24px ${alpha(projColor, 0.4)}`
                              : `0 0 0 2px ${alpha(projColor, 0.7)}, 0 8px 20px ${alpha("#000", 0.18)}, 0 0 20px ${alpha(projColor, 0.3)}`,
                          },
                        }}
                      >
                        {u?.user?.username?.charAt(0)}
                      </Avatar>
                    </Tooltip>
                  ))}
                {((task.assignedUsers as MaybeUser[]) ?? []).length >
                  MAX_ASSIGNEE_AVATARS && (
                  <Tooltip title="Show all assignees" arrow>
                    <Avatar
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenAssignees(
                          (task.assignedUsers as MaybeUser[]) ?? [],
                          task.title
                        );
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        border: `2.5px solid ${theme.palette.background.paper}`,
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 900,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
                        color: theme.palette.primary.main,
                        boxShadow: theme.palette.mode === "dark"
                          ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}, 0 4px 12px ${alpha("#000", 0.4)}`
                          : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.5)}, 0 4px 12px ${alpha("#000", 0.12)}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "scale(1.2) translateY(-2px)",
                          zIndex: 999,
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.4)} 0%, ${alpha(theme.palette.primary.main, 0.3)} 100%)`,
                          boxShadow: theme.palette.mode === "dark"
                            ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.6)}, 0 8px 20px ${alpha("#000", 0.5)}`
                            : `0 0 0 2px ${alpha(theme.palette.primary.main, 0.7)}, 0 8px 20px ${alpha("#000", 0.18)}`,
                        },
                      }}
                    >
                      +
                      {((task.assignedUsers as MaybeUser[]) ?? []).length -
                        MAX_ASSIGNEE_AVATARS}
                    </Avatar>
                  </Tooltip>
                )}
              </Box>

              {/* Action buttons - Enhanced with modern styling */}
              <Box display="flex" alignItems="center" gap={0.5}>
                {/* Comments (badge) */}
                <Tooltip title="Comments" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onComment(task.id);
                      }}
                      sx={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          bgcolor: theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.08),
                          transform: "scale(1.15)",
                        },
                      }}
                    >
                      <Badge
                        badgeContent={commentCount > 0 ? commentCount : 0}
                        color="primary"
                        invisible={commentCount <= 0}
                        overlap="circular"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontWeight: 700,
                            fontSize: 9,
                            minWidth: 16,
                            height: 16,
                            boxShadow: theme.palette.mode === "dark"
                              ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                          },
                        }}
                      >
                        <CommentIcon
                          sx={{
                            fontSize: 17,
                            color: commentCount > 0
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            transition: "color 0.2s ease",
                          }}
                        />
                      </Badge>
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Reminder hint */}
                <Tooltip
                  title={reminder ? `Reminder: ${reminder}` : "No reminder set"}
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          bgcolor: reminder 
                            ? (theme.palette.mode === "dark"
                              ? alpha(theme.palette.primary.main, 0.15)
                              : alpha(theme.palette.primary.main, 0.08))
                            : alpha(theme.palette.text.primary, 0.05),
                          transform: "scale(1.15)",
                        },
                      }}
                    >
                      <NotificationsActiveIcon
                        sx={{
                          fontSize: 17,
                          color: reminder
                            ? theme.palette.primary.main
                            : theme.palette.text.disabled,
                          transition: "color 0.2s ease",
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Privacy toggle */}
                <Tooltip
                  title={
                    task?.public
                      ? "Public (click to make private)"
                      : "Private (click to make public)"
                  }
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrivacy(task.id, !task.public);
                    }}
                    sx={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        bgcolor: task?.public
                          ? (theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.08))
                          : alpha(theme.palette.text.primary, 0.05),
                        transform: task?.public ? "scale(1.15)" : "scale(1.15) rotate(5deg)",
                      },
                    }}
                  >
                    {task?.public ? (
                      <PublicIcon
                        sx={{ 
                          fontSize: 17, 
                          color: theme.palette.primary.main,
                          transition: "color 0.2s ease",
                        }}
                      />
                    ) : (
                      <LockIcon
                        sx={{ 
                          fontSize: 17, 
                          color: theme.palette.text.secondary,
                          transition: "color 0.2s ease",
                        }}
                      />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Follow toggle (badge) */}
                <Tooltip
                  title={
                    isFollowed
                      ? "Following (unfollow)"
                      : "Not following (follow)"
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFollow(task.id, isFollowed);
                      }}
                      sx={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          bgcolor: theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.08),
                          transform: "scale(1.15)",
                        },
                      }}
                    >
                      <Badge
                        badgeContent={followerCount > 0 ? followerCount : 0}
                        color="primary"
                        invisible={followerCount <= 0}
                        overlap="circular"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontWeight: 700,
                            fontSize: 9,
                            minWidth: 16,
                            height: 16,
                            boxShadow: theme.palette.mode === "dark"
                              ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                          },
                        }}
                      >
                        {isFollowed ? (
                          <PersonIcon
                            sx={{ 
                              fontSize: 17, 
                              color: theme.palette.primary.main,
                              transition: "color 0.2s ease",
                            }}
                          />
                        ) : (
                          <PersonOutlineIcon
                            sx={{
                              fontSize: 17,
                              color: followerCount > 0
                                ? theme.palette.primary.main
                                : theme.palette.text.disabled,
                              transition: "color 0.2s ease",
                            }}
                          />
                        )}
                      </Badge>
                    </IconButton>
                  </span>
                </Tooltip>

                {/* Edit */}
                <Tooltip title="Edit" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(task.id);
                    }}
                    sx={{
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        bgcolor: theme.palette.mode === "dark"
                          ? alpha(theme.palette.warning.main, 0.15)
                          : alpha(theme.palette.warning.main, 0.08),
                        transform: "scale(1.15) rotate(-5deg)",
                        color: theme.palette.warning.main,
                      },
                    }}
                  >
                    <EditIcon 
                      sx={{ 
                        fontSize: 17,
                        transition: "color 0.2s ease",
                      }} 
                    />
                  </IconButton>
                </Tooltip>

                {/* Attachments (badge) */}
                <Tooltip title="Attachments" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttach(task.id);
                      }}
                      sx={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          bgcolor: theme.palette.mode === "dark"
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.main, 0.08),
                          transform: "scale(1.15) rotate(15deg)",
                        },
                      }}
                    >
                      <Badge
                        badgeContent={attachCount > 0 ? attachCount : 0}
                        color="primary"
                        invisible={attachCount <= 0}
                        overlap="circular"
                        sx={{
                          "& .MuiBadge-badge": {
                            fontWeight: 700,
                            fontSize: 9,
                            minWidth: 16,
                            height: 16,
                            boxShadow: theme.palette.mode === "dark"
                              ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`
                              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                          },
                        }}
                      >
                        <AttachFileIcon
                          sx={{
                            fontSize: 17,
                            color: attachCount > 0
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            transition: "color 0.2s ease",
                          }}
                        />
                      </Badge>
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Grid>
    );
  }
);

TaskCard.displayName = "TaskCard";

/* -------------------------------- Component ------------------------------- */
const TasksGrid: React.FC<TasksGridProps> = ({
  rows,
  isLoading,
  handleComment,
  handlePrivacy,
  handleFollow,
  handleEdit,
  handleAttach,
  currentUserId,
  density = "comfortable",
  onRefresh,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const compact = density === "compact";

  // Assignees dialog
  const [assigneesDialog, setAssigneesDialog] = useState<{
    open: boolean;
    users: MaybeUser[];
    taskTitle?: string;
  }>({ open: false, users: [] });

  // Change status dialog with comment
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    taskId: number | null;
    current: TaskStatus | "";
    saving: boolean;
    comment: string;
  }>({ open: false, taskId: null, current: "", saving: false, comment: "" });
  
  // Helper function to get userTask status display
  const getUserTaskStatusDisplay = (status: string) => {
    switch (status) {
      case "TODO":
        return { 
          label: "To Do", 
          icon: <HourglassBottomIcon fontSize="small" />, 
          color: theme.palette.grey[500] 
        };
      case "IN_PROGRESS":
        return { 
          label: "In Progress", 
          icon: <TimelapseIcon fontSize="small" />, 
          color: theme.palette.warning.main 
        };
      case "DONE":
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
      case "POSTPONED":
        return { 
          label: "Postponed", 
          icon: <DoNotDisturbOnIcon fontSize="small" />, 
          color: theme.palette.error.main 
        };
      default:
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
    }
  };

  // Dates dialog
  const [dateDialog, setDateDialog] = useState<{
    open: boolean;
    start?: string | null;
    end?: string | null;
    title?: string;
  }>({ open: false });

  // Local status overrides so pill updates immediately (currently unused but kept for future use)
  // const [localStatuses, setLocalStatuses] = useState<
  //   Record<number, TaskStatus>
  // >({});

  // Memoized callback handlers for better performance
  const openAssignees = useCallback(
    (users: MaybeUser[], taskTitle?: string) =>
      setAssigneesDialog({ open: true, users, taskTitle }),
    []
  );
  const closeAssignees = useCallback(
    () => setAssigneesDialog({ open: false, users: [], taskTitle: "" }),
    []
  );

  const openStatusChanger = useCallback(
    (taskId: number, current: TaskStatus) =>
      setStatusDialog({ open: true, taskId, current, saving: false, comment: "" }),
    []
  );
  const closeStatusChanger = useCallback(
    () =>
      setStatusDialog({
        open: false,
        taskId: null,
        current: "",
        saving: false,
        comment: "",
      }),
    []
  );

  const openDates = useCallback(
    (start?: string | null, end?: string | null, title?: string) =>
      setDateDialog({
        open: true,
        start: start ?? null,
        end: end ?? null,
        title,
      }),
    []
  );
  const closeDates = useCallback(() => setDateDialog({ open: false }), []);

  const handleNavigate = useCallback(
    (taskId: number) => {
      navigate(`/task/${taskId}`);
    },
    [navigate]
  );

  const handleApplyStatus = async () => {
    if (!statusDialog.taskId || !statusDialog.current) return;
    try {
      setStatusDialog((s) => ({ ...s, saving: true }));
      const token = localStorage.getItem("accessToken");
      await axios.patch(
        `${baseUrl}/tasks/usertask-status/${statusDialog.taskId}/${currentUserId}`,
        { 
          status: statusDialog.current,
          comment: statusDialog.comment || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeStatusChanger();
      // Refresh the grid data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Status update error:", error);
      setStatusDialog((s) => ({ ...s, saving: false }));
    }
  };

  const statusOptions = useMemo(
    () =>
      [
        TaskStatus.TODO,
        TaskStatus.IN_PROGRESS,
        TaskStatus.REVIEW,
        TaskStatus.DONE,
        TaskStatus.POSTPONED,
      ] as TaskStatus[],
    []
  );

  /* --------------------------------- UI ----------------------------------- */

  // Optional: skeletons feel nicer than a lone spinner
  const useSkeletonWhileLoading = true;
  if (isLoading) {
    return useSkeletonWhileLoading ? (
      <Box sx={{ height: "100%", overflowY: "auto", p: compact ? 1.25 : 2 }}>
        <Grid container spacing={compact ? 1.25 : 2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid key={i} item xs={12} sm={6} md={4} lg={3} xl={2.4 as any}>
              <Box
                sx={{
                  p: compact ? 1.25 : 2,
                  minHeight: compact ? 280 : 340,
                  borderRadius: 2.5,
                  border: (t) => `1px solid ${t.palette.divider}`,
                }}
              >
                <Skeleton
                  variant="text"
                  width="40%"
                  height={22}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="text"
                  width="90%"
                  height={28}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={18}
                  sx={{ mb: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={60}
                  sx={{ borderRadius: 2, mb: 1 }}
                />
                <Skeleton
                  variant="rectangular"
                  height={6}
                  sx={{ borderRadius: 999 }}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 1.25 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    ) : (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100%", overflowY: "auto", p: compact ? 1.25 : 2 }}>
        {rows.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              minHeight: 260,
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              color: "text.secondary",
            }}
          >
            <InfoOutlinedIcon />
            <Typography variant="body2">
              No tasks match your filters yet.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={compact ? 1.25 : 2}>
            {rows.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                currentUserId={currentUserId}
                density={density}
                theme={theme}
                onComment={handleComment}
                onPrivacy={handlePrivacy}
                onFollow={handleFollow}
                onEdit={handleEdit}
                onAttach={handleAttach}
                onNavigate={handleNavigate}
                onOpenAssignees={openAssignees}
                onOpenStatusChanger={openStatusChanger}
                onOpenDates={openDates}
              />
            ))}
          </Grid>
        )}
      </Box>

      {/* Assignees Dialog */}
      <Dialog
        open={assigneesDialog.open}
        onClose={closeAssignees}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Assignees
          {assigneesDialog.taskTitle ? ` · ${assigneesDialog.taskTitle}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {assigneesDialog.users.length ? (
            <List dense>
              {assigneesDialog.users.map((u, i) => (
                <ListItem key={u?.user?.id ?? i}>
                  <ListItemAvatar>
                    <Avatar src={u?.user?.profile?.imageUrl || undefined}>{u?.user?.username?.charAt(0)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{
                      noWrap: true,
                      title: u?.user?.username || "—",
                    }}
                    primary={u?.user?.username || "—"}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No assignees.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignees}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Change Status Dialog with Comment */}
      <Dialog
        open={statusDialog.open}
        onClose={statusDialog.saving ? undefined : closeStatusChanger}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Change My Task Status
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" size="small" sx={{ mb: 2 }}>
            <InputLabel id="status-select">New Status</InputLabel>
            <Select
              labelId="status-select"
              value={statusDialog.current || ""}
              onChange={(e) =>
                setStatusDialog((s) => ({
                  ...s,
                  current: e.target.value as TaskStatus,
                }))
              }
              label="New Status"
            >
              {statusOptions
                .filter((s) => s !== TaskStatus.REVIEW && s !== TaskStatus.ALL)
                .map((s) => (
                  <MenuItem key={s} value={s}>
                    {getTaskStatusLabel(s)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <DialogContentText sx={{ mb: 2, color: "text.secondary" }}>
            Add a comment or notes about this status change (optional):
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Enter your comment or notes..."
            value={statusDialog.comment}
            onChange={(e) => setStatusDialog((s) => ({ ...s, comment: e.target.value }))}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          {statusDialog.current && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main, display: "block", mb: 0.5 }}>
                New Status:
              </Typography>
              <Chip
                label={getUserTaskStatusDisplay(statusDialog.current).label}
                size="small"
                icon={getUserTaskStatusDisplay(statusDialog.current).icon}
                sx={{
                  backgroundColor: alpha(getUserTaskStatusDisplay(statusDialog.current).color, 0.15),
                  color: getUserTaskStatusDisplay(statusDialog.current).color,
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeStatusChanger}
            disabled={statusDialog.saving}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyStatus}
            disabled={statusDialog.saving || !statusDialog.current}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
            }}
          >
            {statusDialog.saving ? <CircularProgress size={20} /> : "Save Status"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dates Dialog */}
      <Dialog
        open={dateDialog.open}
        onClose={closeDates}
        sx={{ ".MuiDialog-paper": { width: 420 } }}
      >
        <DialogTitle>
          Task Dates{dateDialog.title ? ` · ${dateDialog.title}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Box
            display="flex"
            flexDirection={isSmDown ? "column" : "column"}
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={1.25}>
              <CalendarMonthIcon fontSize="small" />
              <Typography fontWeight={700} variant="body2">
                Start:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dateDialog.start ? handleDate(dateDialog.start) : "—"}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.25}>
              <CalendarMonthIcon fontSize="small" />
              <Typography fontWeight={700} variant="body2">
                End:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dateDialog.end ? handleDate(dateDialog.end) : "—"}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDates}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TasksGrid;
